import React from 'react'
import classNames from 'classnames'
import { InternalLink } from '@/atoms/InternalLink'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { Footer } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { useThemeContext } from '@/contexts/ThemeContext'
import { ExperimentVariantPicker } from '@/organisms/Footer/ExperimentVariantPicker'
import { getCurrentYear } from '@/utils/date-utils'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { Maybe } from '@/utils/types'

interface MinimalistFooterProps {
  className?: string
  footerText?: Maybe<string>
}

export const MinimalistFooter: React.FC<MinimalistFooterProps> = ({ className, footerText }) => {
  const { isDarkMode } = useThemeContext()
  const { t } = useTranslate('common')
  const currentYear = getCurrentYear()

  return (
    <footer
      className={classNames(
        'relative z-10',
        isDarkMode ? 'bg-black text-core-gray-100' : 'bg-white text-core-gray-950',
        className,
      )}
    >
      <PaddedContainer>
        <div className="flex flex-col flex-wrap items-center justify-between gap-2 pb-16 pt-8 text-center text-core-gray-500 md:flex-row">
          <ExperimentVariantPicker key="experiment-variant-picker" logoColor={isDarkMode ? 'white' : 'black'} />
          <ul className="md:order-0 order-1 mt-2 flex gap-4 underline md:flex-row">
            <li>
              <InternalLink href={paths.legal.privacyPolicy} data-testid="footer-link">
                <Footer>
                  <Translate t={t} i18nKey="privacyPolicy">
                    Privacy Policy
                  </Translate>
                </Footer>
              </InternalLink>
            </li>
            <li>
              <InternalLink href={paths.legal.termsOfUse} data-testid="footer-link">
                <Footer>
                  <Translate t={t} i18nKey="termsOfUse">
                    Terms of Use
                  </Translate>
                </Footer>
              </InternalLink>
            </li>
          </ul>
          <Footer>{footerText}</Footer>
          <div className="md:order-0 order-1 max-w-[425px] text-xs">
            <p>
              <Footer>
                <Translate t={t} i18nKey="angelStudiosIsANewKindOfStudiov3">
                  Angel is a new kind of streaming platform—powered by over 1 million Angel Guild members, driven by
                  purpose, and built to share stories that amplify light.
                </Translate>
              </Footer>
            </p>
            <p className="mt-6 uppercase">
              <Footer>
                <Translate t={t} i18nKey="copyrightAngel" values={{ currentYear }}>
                  Copyright © {{ currentYear }} by Angel, All Rights Reserved
                </Translate>
              </Footer>
            </p>
          </div>
        </div>
      </PaddedContainer>
    </footer>
  )
}
